import React, { useState } from "react";
import {
  Box,
  Chip,
  Collapse,
  Divider,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import SourceIcon from "@mui/icons-material/Source";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CommitIcon from "@mui/icons-material/Commit";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { JsonEditor } from "json-edit-react";
import { maskSecrets, scrubUrlCreds, maskToken } from "../../../utils/secrets";

type AnyObj = Record<string, any>;

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.4 }}>
    <Typography
      sx={{ width: 92, flexShrink: 0, fontSize: "0.78rem", color: "text.secondary" }}
    >
      {label}
    </Typography>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Stack>
);

const SourceCard: React.FC<{ name: string; data: AnyObj }> = ({
  name,
  data,
}) => {
  const repo = data?.repository;
  const fetch = data?.fetch || {};
  const auth = data?.authentication || {};
  const token = auth?.token;
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
        <SourceIcon fontSize="small" sx={{ color: "primary.main" }} />
        <Typography sx={{ fontWeight: 800, textTransform: "capitalize" }}>
          {name}
        </Typography>
      </Stack>
      <Stack spacing={0.2}>
        {repo && (
          <Row label="Repository">
            <Link
              href={scrubUrlCreds(repo)}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontSize: "0.82rem", wordBreak: "break-all", fontWeight: 600 }}
            >
              {scrubUrlCreds(repo)}
            </Link>
          </Row>
        )}
        {(fetch.branch || fetch.type) && (
          <Row label="Branch / tag">
            <Stack direction="row" spacing={0.75} alignItems="center">
              <AccountTreeOutlinedIcon
                sx={{ fontSize: 15, color: "text.secondary" }}
              />
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 700 }}>
                {fetch.branch || "—"}
              </Typography>
              {fetch.type && (
                <Chip
                  size="small"
                  label={fetch.type}
                  variant="outlined"
                  sx={{ height: 18 }}
                />
              )}
            </Stack>
          </Row>
        )}
        {(auth.type || auth.username || token) && (
          <Row label="Auth">
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ flexWrap: "wrap", gap: 0.75 }}
            >
              {auth.type && (
                <Chip size="small" label={auth.type} sx={{ height: 20 }} />
              )}
              {auth.username && (
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<PersonOutlineIcon sx={{ fontSize: 14 }} />}
                  label={auth.username}
                  sx={{ height: 20 }}
                />
              )}
              {token && (
                <Tooltip title="Token is hidden for security">
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<VpnKeyOutlinedIcon sx={{ fontSize: 14 }} />}
                    label={maskToken(token)}
                    sx={{ height: 20, fontFamily: "monospace" }}
                  />
                </Tooltip>
              )}
            </Stack>
          </Row>
        )}
      </Stack>
    </Paper>
  );
};

const SuiteMetaViewer: React.FC<{ data?: AnyObj }> = ({ data }) => {
  const [showRaw, setShowRaw] = useState(false);
  const meta: AnyObj = data || {};

  const sources: [string, AnyObj][] = Object.entries(
    meta?.config?.suite || {}
  ).filter(([, v]) => v && typeof v === "object") as [string, AnyObj][];

  // Scalar build/run fields shown at the top.
  const scalarFields = Object.entries(meta).filter(
    ([k, v]) =>
      k !== "config" &&
      (typeof v === "string" || typeof v === "number" || typeof v === "boolean")
  );

  const copy = (v: string) => navigator.clipboard.writeText(v);

  const hasStructured = sources.length > 0 || scalarFields.length > 0;

  return (
    <Box>
      {scalarFields.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <CommitIcon fontSize="small" sx={{ color: "text.secondary" }} />
            <Typography sx={{ fontWeight: 800, fontSize: "0.9rem" }}>
              Build
            </Typography>
          </Stack>
          {scalarFields.map(([k, v]) => {
            const isHash = /hash|commit/i.test(k);
            const display = scrubUrlCreds(String(v));
            return (
              <Row key={k} label={k}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography
                    sx={{
                      fontSize: "0.82rem",
                      fontWeight: 700,
                      fontFamily: isHash ? "monospace" : undefined,
                      wordBreak: "break-all",
                    }}
                  >
                    {display}
                  </Typography>
                  {isHash && (
                    <IconButton size="small" onClick={() => copy(display)}>
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </Stack>
              </Row>
            );
          })}
        </Paper>
      )}

      {sources.length > 0 && (
        <>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "0.8rem",
              textTransform: "uppercase",
              letterSpacing: 0.4,
              color: "text.secondary",
              mb: 1,
            }}
          >
            Sources
          </Typography>
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            {sources.map(([name, src]) => (
              <SourceCard key={name} name={name} data={src} />
            ))}
          </Stack>
        </>
      )}

      {!hasStructured && (
        <Typography color="text.secondary" sx={{ mb: 2, fontSize: "0.85rem" }}>
          No structured metadata for this suite.
        </Typography>
      )}

      <Button
        size="small"
        startIcon={<CodeIcon />}
        onClick={() => setShowRaw((s) => !s)}
        sx={{ color: "text.secondary" }}
      >
        {showRaw ? "Hide raw JSON" : "Show raw JSON"}
      </Button>
      <Collapse in={showRaw} unmountOnExit>
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.level1",
          }}
        >
          <JsonEditorMasked data={meta} />
        </Box>
      </Collapse>
    </Box>
  );
};

// Feed JsonEditor secret-masked data so tokens never render in plaintext.
const JsonEditorMasked: React.FC<{ data: AnyObj }> = ({ data }) => (
  <JsonEditor
    rootName="meta"
    minWidth="100%"
    collapse={2}
    data={maskSecrets(data) ?? {}}
    restrictAdd
    restrictDelete
    restrictEdit
  />
);

export default SuiteMetaViewer;
